import { Button } from 'antd';
import { AnchorSimple, CheckCircle, DeviceMobile } from '@phosphor-icons/react';
import type { WmsShipmentInboundType } from 'schemas/shipment/inbound/detail';
import { COLOR } from '@styles/theme';
import {
  canMarkShipmentArrived,
  getEffectiveShipmentStatus,
} from '@utility/utils/shipment-display';
import { InboundIntakeStepPanel } from './InboundIntakeStepPanel';
import { InboundShipmentStorageAssignmentStep } from './InboundShipmentStorageAssignmentStep';

type InboundShipmentIntakeTabProps = {
  data: WmsShipmentInboundType;
  stepIndex: number;
  onMarkArrived?: () => void;
  markingArrived?: boolean;
  onMarkCompleted?: () => void;
  markingCompleted?: boolean;
};

export function InboundShipmentIntakeTab({
  data,
  stepIndex,
  onMarkArrived,
  markingArrived = false,
  onMarkCompleted,
  markingCompleted = false,
}: InboundShipmentIntakeTabProps) {
  if (stepIndex === 2) {
    return (
      <InboundShipmentStorageAssignmentStep
        pallets={data.pallets}
        onMarkCompleted={onMarkCompleted}
        markingCompleted={markingCompleted}
      />
    );
  }

  const status = getEffectiveShipmentStatus(data);
  const canMarkArrived =
    stepIndex === 0 && canMarkShipmentArrived(status) && Boolean(onMarkArrived);

  switch (stepIndex) {
    case 0:
      return (
        <InboundIntakeStepPanel
          icon={<AnchorSimple size={32} color={COLOR.accent} />}
          title="Pending arrival"
          description="This shipment has not been marked as arrived at the warehouse yet. Confirm arrival when the freight is on site."
          action={
            canMarkArrived ? (
              <Button type="primary" loading={markingArrived} onClick={onMarkArrived}>
                Mark as arrived
              </Button>
            ) : undefined
          }
        />
      );
    case 1:
      return (
        <InboundIntakeStepPanel
          icon={<DeviceMobile size={32} color={COLOR.accent} />}
          title="Item count in progress"
          description="Items are being counted by the warehouse team on the mobile app. Counts will appear on the Contents tab as lines are completed."
        />
      );
    case 3:
    default:
      return (
        <InboundIntakeStepPanel
          icon={<CheckCircle size={32} color={COLOR.success} />}
          title="Complete"
          description="Intake is complete. All items have been counted and stored for this inbound shipment."
        />
      );
  }
}
